import React, { useEffect, useState } from "react";
import supabase from "../config/supabaseConfig";
import { getDataAtual } from "../services/aulas.service";
import { matriculasService } from "../services/matriculas.service";
import { Badge } from "../design-system";
import "../styles/Dashboard.css";

// Dashboard (patch 2026) — layout de docs/prototipos/dashboard.html.
// Números reais via Supabase; "Faltas registradas" conta faltas do dia
// (aula_alunos.status='falta' com aula.data=hoje). Feedback Tales 07/07:
// sem botão Faltou aqui (foi pro card da Sala) e sem tempo de aula no card.
const temRestricao = (aluno) =>
  aluno.lesao && aluno.lesao !== "Nao" && aluno.lesao !== "Não";

const Dashboard = () => {
  const [totalAlunos, setTotalAlunos] = useState(null);
  const [totalAulasAndamento, setTotalAulasAndamento] = useState(null);
  const [aulasAndamento, setAulasAndamento] = useState([]);
  const [totalFaltas, setTotalFaltas] = useState(null);
  // Avaliações pendentes (14/07, pedido Rodrigo: NOMES, não somatória) —
  // atrasadas + as que vencem nos próximos 7 dias.
  const [avalAtrasadas, setAvalAtrasadas] = useState([]);
  const [avalProximas, setAvalProximas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  // ids de avaliação com update em andamento — desabilita o botão só daquele item.
  const [avalSalvando, setAvalSalvando] = useState([]);

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      // Data LOCAL (BRT) — as aulas agora gravam `data` em local (fix 22/07);
      // getDataAtual é a fonte única, casando esta leitura com a gravação.
      const hojeISO = getDataAtual();

      try {
        const [alunosCount, aulas, faltasCount] = await Promise.all([
          supabase
            .from("alunos")
            .select("id", { count: "exact", head: true })
            .eq("status", "ativo"),
          supabase
            .from("aulas")
            .select("id, created_at, professor:professor_id(id, nome)")
            .eq("status", "em_andamento"),
          // Faltas do dia: aula_alunos.status='falta' cujas aulas têm data=hoje.
          // count exato via head:true (sem trazer linhas / sem cap de 1000).
          supabase
            .from("aula_alunos")
            .select("id, aulas!inner(data)", { count: "exact", head: true })
            .eq("status", "falta")
            .eq("aulas.data", hojeISO),
        ]);

        if (!ativo) return;
        setTotalAlunos(alunosCount.count ?? 0);
        setTotalFaltas(faltasCount.error ? 0 : faltasCount.count ?? 0);

        // Avaliações pendentes até hoje+7 (data_realizada null), com o nome
        // do aluno — separadas em atrasadas (< hoje) e próximas (hoje..+7).
        const limite = new Date();
        limite.setUTCDate(limite.getUTCDate() + 7);
        const limiteISO = limite.toISOString().slice(0, 10);
        // !inner + filtro de status: aluno INATIVO não gera alerta de
        // avaliação (caso real: aluno de teste inativo aparecia no card).
        const { data: pendentes } = await supabase
          .from("avaliacoes")
          .select(
            "id, data_agendada, tipo, matricula_id, alunos!inner(nome, status)"
          )
          .is("data_realizada", null)
          .eq("alunos.status", "ativo")
          .lte("data_agendada", limiteISO)
          .order("data_agendada", { ascending: true });
        if (!ativo) return;
        const lista = pendentes || [];
        setAvalAtrasadas(lista.filter((a) => a.data_agendada < hojeISO));
        setAvalProximas(lista.filter((a) => a.data_agendada >= hojeISO));

        const listaAulas = aulas.data || [];
        setTotalAulasAndamento(listaAulas.length);
        const aulaIds = listaAulas.map((a) => a.id);
        let porAula = new Map();

        if (aulaIds.length > 0) {
          const { data: aa } = await supabase
            .from("aula_alunos")
            .select("aula_id, aluno_id")
            .in("aula_id", aulaIds);

          const alunoIds = [...new Set((aa || []).map((r) => r.aluno_id))];
          let alunosMap = new Map();
          if (alunoIds.length > 0) {
            const { data: alunosDetalhe } = await supabase
              .from("alunos")
              .select("id, nome, plano, nivel, lesao, tipo_lesao")
              .in("id", alunoIds);
            alunosMap = new Map((alunosDetalhe || []).map((a) => [a.id, a]));
          }

          (aa || []).forEach((r) => {
            const aluno = alunosMap.get(r.aluno_id);
            if (!aluno) return;
            if (!porAula.has(r.aula_id)) porAula.set(r.aula_id, []);
            porAula.get(r.aula_id).push(aluno);
          });
        }

        const cards = listaAulas.flatMap((aula) =>
          (porAula.get(aula.id) || []).map((aluno) => ({
            ...aluno,
            professorNome: aula.professor?.nome || "—",
          }))
        );

        if (ativo) setAulasAndamento(cards);
      } catch (e) {
        console.error("Erro ao carregar dashboard:", e);
      } finally {
        if (ativo) setCarregando(false);
      }
    };

    carregar();
    return () => {
      ativo = false;
    };
  }, []);

  // Marca a avaliação como realizada (data_realizada = hoje) e some da lista
  // local sem refetch — mantém o item se der erro (ex.: RLS/rede).
  const marcarAvaliacaoRealizada = async (item, deLista, setLista) => {
    const nome = item.alunos?.nome || "esse aluno";
    if (!window.confirm(`Marcar a avaliação de ${nome} como realizada?`)) return;

    setAvalSalvando((prev) => [...prev, item.id]);
    const hoje = new Date().toISOString().slice(0, 10);
    const { error } = await supabase
      .from("avaliacoes")
      .update({ data_realizada: hoje })
      .eq("id", item.id);

    if (error) {
      console.error("Erro ao marcar avaliação como realizada:", error);
      window.alert(`Não foi possível marcar como realizada: ${error.message}`);
    } else {
      // Fecha o ciclo do modelo rolante (27/07): a data vira a "última
      // avaliação" da matrícula e a próxima já é agendada. Sem isto, dar baixa
      // não produzia nada e a agenda do aluno parava aqui.
      if (item.matricula_id) {
        try {
          await matriculasService.registrarAvaliacaoRealizada(
            item.matricula_id,
            hoje
          );
        } catch (errAgenda) {
          // A baixa em si já está gravada — não desfazemos por causa da
          // agenda. Avisa pra ninguém achar que a próxima foi criada.
          console.error("Erro ao agendar a próxima avaliação:", errAgenda);
          window.alert(
            "Avaliação marcada como realizada, mas não foi possível agendar a próxima. " +
              "Abra a ficha do aluno e salve para regenerar. Detalhe: " +
              errAgenda.message
          );
        }
      }
      setLista(deLista.filter((a) => a.id !== item.id));
    }
    setAvalSalvando((prev) => prev.filter((id) => id !== item.id));
  };

  const stats = [
    { lbl: "Total de alunos", num: totalAlunos ?? "…" },
    {
      lbl: "Aulas em andamento",
      num: totalAulasAndamento ?? "…",
      accent: true,
    },
    { lbl: "Alunos em aula", num: carregando ? "…" : aulasAndamento.length },
    {
      lbl: "Faltas registradas",
      num: totalFaltas ?? "…",
    },
  ];

  return (
    <div className="dash-nova">
      <h1>Dashboard</h1>

      <div className="dash-stats">
        {stats.map((s) => (
          <div
            key={s.lbl}
            className={`dash-stat ${s.accent ? "accent" : ""}`}
          >
            <div className="dash-lbl">
              {s.lbl}
              {s.preview && <Badge variant="outline">preview · fase 3</Badge>}
            </div>
            <div className="dash-num">{s.num}</div>
          </div>
        ))}
      </div>

      {/* Avaliações a vencer/atrasadas — NOMES (pedido Rodrigo 14/07). O
          gatilho de WhatsApp (proposta separada) vai ler as mesmas pendências. */}
      {!carregando && (avalAtrasadas.length > 0 || avalProximas.length > 0) && (
        <>
          <div className="dash-section-head">
            <h2>Avaliações</h2>
          </div>
          <div className="dash-aval">
            {avalAtrasadas.map((a) => (
              <div key={a.id} className="dash-aval-item atrasada">
                <span className="dash-aval-nome">{a.alunos?.nome || "—"}</span>
                <span className="dash-aval-data">
                  {a.data_agendada?.split("-").reverse().join("/")} · atrasada
                </span>
                <button
                  type="button"
                  className="dash-aval-check"
                  disabled={avalSalvando.includes(a.id)}
                  onClick={() =>
                    marcarAvaliacaoRealizada(a, avalAtrasadas, setAvalAtrasadas)
                  }
                >
                  ✓ Realizada
                </button>
              </div>
            ))}
            {avalProximas.map((a) => (
              <div key={a.id} className="dash-aval-item">
                <span className="dash-aval-nome">{a.alunos?.nome || "—"}</span>
                <span className="dash-aval-data">
                  {a.data_agendada?.split("-").reverse().join("/")}
                  {a.tipo === "inicial" ? " · 1ª avaliação" : ""}
                </span>
                <button
                  type="button"
                  className="dash-aval-check"
                  disabled={avalSalvando.includes(a.id)}
                  onClick={() =>
                    marcarAvaliacaoRealizada(a, avalProximas, setAvalProximas)
                  }
                >
                  ✓ Realizada
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="dash-section-head">
        <h2>Alunos em aula</h2>
      </div>

      {!carregando && aulasAndamento.length === 0 && (
        <p className="dash-vazio">Nenhum aluno em aula no momento.</p>
      )}

      <div className="dash-cards">
        {aulasAndamento.map((a) => (
          <div
            key={a.id}
            className={`dash-card ${temRestricao(a) ? "lesao" : ""}`}
          >
            <h3>{a.nome}</h3>
            <div className="dash-meta">
              Prof. {a.professorNome} · {a.plano || "—"}
            </div>
            <div className="dash-foot">
              {temRestricao(a) ? (
                <Badge
                  variant="warning"
                  className="dash-badge-trunc"
                  title={a.tipo_lesao || "Restrição"}
                >
                  {a.tipo_lesao || "Restrição"}
                </Badge>
              ) : (
                <Badge variant="outline">{a.nivel || "—"}</Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
